import { useDesignXML } from "@/hooks/useDesignXML";
import type { StructureSummary } from "@/types/structure";
import { Button } from "@/components/ui/button";
import { StepCardWrapper } from "../StepCardWrapper";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pencil, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { FileButton } from "@/components/shared/FileButton";
import { useUpdateDesign, useUploadDesign } from "@/hooks/useSpecLists";
import { toast } from "react-toastify";
import { BreakingChangesErrorToast } from "./components/BreakingChangesErrorToast";
import { BreakingChangesDialog } from "./components/dialogs/BreakingChangesDialog";
import { DesignOverviewForm } from "./components/DesignOverviewForm";
import { DesignUpdatePreviewDialog } from "./components/dialogs/DesignUpdatePreviewDialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDeleteMaskXMLDesigns, useDeleteMaskCSVDesigns } from "@/hooks/useMasks";
import { BreakingChangesDetail, UploadDesignPreview } from "./DesignUploadStep.types";
import { useDeleteStructure } from "@/hooks/useStructures";
import { usePermissionContext } from "@/context/PermissionContext";
import { DesignDeleteConfirmationDialog } from "./components/dialogs/DesignDeleteConfirmationDialog";
import { DesignDeleteAllConfirmationDialog } from "./components/dialogs/DesignDeleteAllConfirmationDialog";

type DesignUploadStepProps = {
  refreshMaskData: () => void;
  selectedMaskId: string;
};

export function DesignUploadStep({ refreshMaskData, selectedMaskId }: DesignUploadStepProps) {
  const [activeTab, setActiveTab] = useState<"pcm" | "die">("pcm");
  const [searchQuery, setSearchQuery] = useState("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<UploadDesignPreview | null>(null);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [selectedStructure, setSelectedStructure] = useState<StructureSummary | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [breakingChanges, setBreakingChanges] = useState<BreakingChangesDetail | null>(null);

  const [showDesignDialog, setShowDesignDialog] = useState<boolean>(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState<boolean>(false);
  const [showBreakingChangesDialog, setShowBreakingChangesDialog] = useState<boolean>(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [showDeleteAllConfirmationDialog, setShowDeleteAllConfirmationDialog] = useState(false);

  const { hasPermission } = usePermissionContext();

  const { trigger: deleteDesignXML } = useDeleteMaskXMLDesigns(selectedMaskId);
  const { trigger: deleteDesignCSV } = useDeleteMaskCSVDesigns(selectedMaskId);
  const { trigger: deleteStructure } = useDeleteStructure();

  const { trigger: uploadFile } = useUploadDesign();
  const { trigger: updateDesign } = useUpdateDesign();

  const isCustom = activeTab === "die";

  const {
    data: structures,
    error,
    isLoading,
    mutate: refreshStructures,
  } = useDesignXML(selectedMaskId, isCustom);

  const refreshData = () => {
    refreshMaskData();
    refreshStructures();
  };

  const handleDeleteDesignCSV = async () => {
    try {
      await deleteDesignCSV(null, {
        onSuccess: () => {
          refreshData();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
      setShowDeleteAllConfirmationDialog(false);
    } catch (error: any) {
      console.error(error);
    }
  };
  const handleDeleteDesignXML = async () => {
    try {
      await deleteDesignXML(null, {
        onSuccess: () => {
          refreshData();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
      setShowDeleteAllConfirmationDialog(false);
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleDeleteAll = isCustom ? handleDeleteDesignCSV : handleDeleteDesignXML;

  const canDeleteMessage = isCustom ? "Delete all off-PCM designs" : "Delete all PCM designs";

  const cannotDeleteMessage = isCustom
    ? "Cannot delete all off-PCM designs while they are used on a testlabel"
    : "Cannot delete PCM designs when a speclist has been assigned to the mask";

  const handleDelete = async (id: string) => {
    try {
      await deleteStructure(
        { id, mask_id: selectedMaskId },
        {
          onSuccess: () => {
            refreshData();
          },
          onError: (error) => {
            toast.error(error.message);
          },
        },
      );
      setShowDeleteConfirmationDialog(false);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredStructures = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) {
      return structures.sort((a, b) => a.name.localeCompare(b.name));
    }

    return structures
      .filter(
        (structure) =>
          structure.name.toLowerCase().includes(query) ||
          structure.testSiteName.toLowerCase().includes(query),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchQuery, structures]);

  const canDeleteAll = filteredStructures.every((structure) => structure.canDelete);

  const clearFileSelection = () => {
    setFile(undefined);
  };

  const handleFileUpload = async (file: File | undefined) => {
    try {
      if (!file) return;
      setIsUploading(true);

      await uploadFile(
        {
          file,
          mask_id: selectedMaskId,
        },
        {
          onError: (error) => {
            toast.error(error.message);
            clearFileSelection();
          },
          onSuccess: () => {
            toast.success("Successfully uploaded and processed the ZIP file!");
            refreshData();
            clearFileSelection();
          },
        },
      );
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePreviewUpdate = async (file: File | undefined) => {
    try {
      if (!file) return;

      const preview = await updateDesign(
        {
          file,
          mask_id: selectedMaskId,
          preview: true,
        },
        {
          onError: (error) => {
            const detail = error.detail as BreakingChangesDetail | undefined;
            if (
              detail?.structures_removed?.length ||
              detail?.structures_with_name_changes?.length
            ) {
              setBreakingChanges(detail);
              clearFileSelection();
              toast.error(
                <BreakingChangesErrorToast
                  message={error.message}
                  onShowDetails={() => setShowBreakingChangesDialog(true)}
                />,
              );
            } else {
              toast.error(error.message);
            }
          },
        },
      );

      if (preview) {
        setPreviewData(preview as UploadDesignPreview);
        setShowPreviewDialog(true);
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  // Confirm design CSV update after preview
  const handleConfirmUpdate = async () => {
    try {
      if (!file) return;

      setIsConfirming(true);

      await updateDesign(
        {
          file,
          mask_id: selectedMaskId,
          preview: false,
        },
        {
          onError: (error) => {
            toast.error(error.message);
            clearFileSelection();
          },
          onSuccess: () => {
            toast.success("Successfully updated the design CSV!");
            setShowPreviewDialog(false);
            setPreviewData(null);
            refreshData();
            clearFileSelection();
          },
        },
      );
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleShowStructure = (structure: StructureSummary) => {
    setSelectedStructure(structure);
    setShowDesignDialog(true);
  };
  const handleEditStructure = (structure: StructureSummary) => {
    setSelectedStructure(structure);
    setShowDesignDialog(true);
    setIsEditing(true);
  };
  const handleDeleteStructure = (structure: StructureSummary) => {
    setSelectedStructure(structure);
    setShowDeleteConfirmationDialog(true);
  };
  const handleConfirmDelete = (id: string) => {
    handleDelete(id);
    setSelectedStructure(null);
    setShowDeleteConfirmationDialog(false);
  };

  return (
    <>
      <StepCardWrapper isSticky={false} title="Design Upload">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "pcm" | "die")}>
          <TabsList>
            <TabsTrigger value="pcm">On PCM</TabsTrigger>
            <TabsTrigger value="die">Off PCM</TabsTrigger>
          </TabsList>
          <div className="relative mb-0 shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder={`Search ${isCustom ? "Off-PCM" : "PCM"} structures`}
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
              }}
              className="w-full pl-10"
            />
          </div>
          <div className="mb-2 p-0">
            <p className="text-xs text-muted-foreground">
              {filteredStructures.length} {isCustom ? "Off-PCM" : "PCM"} structure
              {filteredStructures.length === 1 ? "" : "s"} total
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
              Loading {isCustom ? "Off-PCM" : "PCM"} structures...
            </div>
          ) : error ? (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error.message}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredStructures.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  No {isCustom ? "Off-PCM" : "PCM"} structures found.
                </div>
              ) : (
                <div className="minimal-scrollbar max-h-[55vh] space-y-2 overflow-y-auto pr-1">
                  {filteredStructures.map((structure) => (
                    <div
                      onClick={() => handleShowStructure(structure)}
                      key={structure.id}
                      className="flex items-center justify-between gap-3 rounded-md border px-3 py-1 cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{structure.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          Test Site: {structure.testSiteName}
                        </p>
                      </div>

                      <div
                        className="flex shrink-0 items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {hasPermission("structures", "update") && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${structure.name}`}
                            onClick={() => handleEditStructure(structure)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                        {hasPermission("structures", "delete") && (
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={!structure.canDelete}
                            onClick={() => handleDeleteStructure(structure)}
                            size="icon"
                            aria-label={`Delete ${structure.name}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex w-full items-center gap-2">
            <div className="flex-1">
              <FileButton
                className="w-full"
                file={file}
                setFile={setFile}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
                label={isCustom ? "Upload CSV" : "Upload ZIP"}
                accept={isCustom ? ".csv" : ".zip"}
                onUpload={filteredStructures.length === 0 ? handleFileUpload : handlePreviewUpdate}
              />
            </div>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={!canDeleteAll}
                  onClick={() => setShowDeleteAllConfirmationDialog(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  ALL
                </Button>
              </TooltipTrigger>

              <TooltipContent>
                {canDeleteAll ? canDeleteMessage : cannotDeleteMessage}
              </TooltipContent>
            </Tooltip>
          </div>
        </Tabs>
      </StepCardWrapper>

      <DesignUpdatePreviewDialog
        preview={previewData}
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        onConfirm={handleConfirmUpdate}
        isConfirming={isConfirming}
      />

      <DesignOverviewForm
        selectedStructure={selectedStructure}
        onOpenChange={setShowDesignDialog}
        open={showDesignDialog}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
      />

      {breakingChanges && (
        <BreakingChangesDialog
          detail={breakingChanges}
          open={showBreakingChangesDialog}
          onOpenChange={setShowBreakingChangesDialog}
        />
      )}

      <DesignDeleteConfirmationDialog
        selectedStructure={selectedStructure}
        setShow={setShowDeleteConfirmationDialog}
        show={showDeleteConfirmationDialog}
        handleDelete={handleConfirmDelete}
      />

      <DesignDeleteAllConfirmationDialog
        setShow={setShowDeleteAllConfirmationDialog}
        show={showDeleteAllConfirmationDialog}
        handleDeleteAll={handleDeleteAll}
      />
    </>
  );
}
